<template>
  <section-card
    data-test="my-env-section-toolruns"
    :data-item-count="itemCount"
    :title="embedded ? t('mijn_omgeving.toolruns.title') : null"
    :subtitle="embedded ? t('mijn_omgeving.toolruns.subtitle') : null"
    :is-empty="!hasItems && !isLoading"
    :is-loading="isLoading && !hasItems"
    :empty-title="t('mijn_omgeving.toolruns.empty_title')"
    :empty-message="t('mijn_omgeving.toolruns.empty_message')"
  >
    <!-- Skeleton: three ghost rows mirroring the snapshot list — status pill,
         tool name fill, date, trailing chevron — so the column height matches
         what arrives. -->
    <template #loadingSkeleton>
      <ul class="divide-y divide-theme-border dark:divide-theme-border -my-1" aria-hidden="true">
        <li v-for="i in 3" :key="`tr-skel-${i}`" class="flex items-center gap-2 py-2 min-w-0">
          <skeleton-text width="44px" height="14px" />
          <div class="flex-1 min-w-0">
            <skeleton-text :width="i === 1 ? '70%' : i === 2 ? '55%' : '80%'" height="13px" />
          </div>
          <skeleton-text width="50px" height="10px" />
          <skeleton-text width="14px" height="14px" variant="circle" />
        </li>
      </ul>
    </template>

    <!-- "Vastgezet" block: pinned tools as quick-access (briefing §6). Full view only. -->
    <div
      v-if="limit === null && pinnedRuns.length > 0"
      class="mb-3 rounded-md border border-dashed border-theme-palette-emerald/40 dark:border-theme-palette-emerald/50 p-2"
    >
      <div class="flex items-center gap-2 mb-1">
        <icon :path="mdiPin" size="14" class="text-theme-palette-emerald dark:text-theme-palette-emerald" />
        <span class="text-[10px] uppercase tracking-wide font-medium text-theme-palette-emerald dark:text-theme-palette-emerald">
          {{ t('mijn_omgeving.toolruns.pinned_block') }}
        </span>
      </div>
      <div class="space-y-1">
        <tool-run-row
          v-for="run in pinnedRuns"
          :key="`pinned-${run.id}`"
          :tool-name="run.tool_label"
          :summary-input="run.summary_input"
          :status="mapStatus(run.status)"
          :started-at="run.started_at"
          :pinned="true"
          @open="onOpen(run)"
          @retry="onRetry(run)"
          @toggle-pin="(v) => onTogglePin(run, v)"
        />
      </div>
    </div>

    <!-- Filter chips — only in full view. -->
    <div v-if="limit === null" class="flex flex-wrap gap-1 mb-2">
      <button
        v-for="cat in categoryChips"
        :key="cat.value || 'all'"
        type="button"
        :class="[
          'inline-flex items-center text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full',
          activeCategory === cat.value
            ? 'bg-theme-brand text-theme-text-inverse'
            : 'bg-theme-bg-subtle text-theme-text-secondary hover:bg-theme-surface-raised dark:bg-theme-surface-raised dark:hover:bg-theme-surface-elevated',
        ]"
        @click="setCategory(cat.value)"
      >
        {{ cat.label }}
      </button>
    </div>

    <!-- Snapshot mode (Home Layer 3): compact one-line rows. The full
         ToolRunRow renders status pill + tool-name + summary + started-at +
         pin + retry + open and is too wide for a quarter-screen snapshot
         column, so we strip down to status pill + tool-name + open here and
         keep the full row for the dedicated /my-environment/toolruns page. -->
    <ul v-if="snapshot" class="divide-y divide-theme-border dark:divide-theme-border -my-1">
      <li
        v-for="run in snapshotRuns"
        :key="run.id"
        class="min-w-0"
      >
        <!-- Whole row clickable. `run.deep_link` carries the tool's route
             with `?run={id}` so we land directly on the result of this run. -->
        <component
          :is="run.deep_link ? 'router-link' : 'div'"
          :to="run.deep_link || undefined"
          class="flex items-center gap-2 py-2 -mx-2 px-2 rounded-md transition-colors hover:bg-theme-palette-emerald/40 dark:hover:bg-theme-palette-emerald/15 group"
        >
          <span
            :class="[
              'shrink-0 inline-flex items-center text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded-full',
              snapshotStatusPillClass(run),
            ]"
          >
            {{ snapshotStatusLabel(run) }}
          </span>
          <span
            class="flex-1 min-w-0 text-sm text-theme-text truncate group-hover:text-theme-palette-emerald dark:group-hover:text-theme-palette-emerald"
            :title="snapshotRunTitle(run)"
          >
            {{ snapshotRunTitle(run) }}
          </span>
          <span
            v-if="snapshotUpdateTs(run)"
            class="shrink-0 text-[11px] text-theme-text-muted tabular-nums"
          >
            {{ snapshotStartedLabel(snapshotUpdateTs(run)) }}
          </span>
          <span
            class="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md
              text-theme-palette-emerald dark:text-theme-palette-emerald"
            aria-hidden="true"
          >
            <icon :path="mdiArrowRight" size="14" />
          </span>
        </component>
      </li>
    </ul>

    <div v-else class="space-y-1">
      <tool-run-row
        v-for="run in unpinnedDisplayed"
        :key="run.id"
        :tool-name="run.tool_label"
        :summary-input="run.summary_input"
        :status="mapStatus(run.status)"
        :started-at="run.started_at"
        :pinned="run.pinned"
        @open="onOpen(run)"
        @retry="onRetry(run)"
        @toggle-pin="(v) => onTogglePin(run, v)"
      />
    </div>
  </section-card>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import SkeletonText from '@/components/ui/states/SkeletonText.vue';
  import SectionCard from '@/components/ui/data-display/SectionCard.vue';
  import ToolRunRow from '@/components/ui/data-display/ToolRunRow.vue';
  import { loadToolruns, pinTool, unpinTool, useToolruns } from '@/pages/my-environment/composables/toolruns';
  import { mdiArrowRight, mdiPin } from '@mdi/js';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const props = defineProps({
    limit: { type: Number, default: null },
    /** When true, this is the Home Layer 3 snapshot — the section already hides
        pinned and filter chips when `limit` is set; the prop is accepted for
        symmetry with the other Mijn* sections used by HomeSnapshotSection. */
    snapshot: { type: Boolean, default: false },
    /** When `false`, the section-card hides its title — used by the
     *  standalone `/my-environment/toolruns` route where the page-level
     *  StandardToolPage header already identifies the section. */
    embedded: { type: Boolean, default: true },
  });

  const router = useRouter();
  const { t } = useI18n();
  const { toolruns, isLoading } = useToolruns();

  const activeCategory = ref(null);

  // "Backtester" appears in the briefing's filter list. We honor it client-side
  // by mapping to the backtester tool's canonical key (bidding-strategy).
  const categoryChips = computed(() => [
    { value: null, label: t('mijn_omgeving.toolruns.filter_all') },
    { value: 'forecasts', label: t('mijn_omgeving.toolruns.filter_forecasts') },
    { value: 'benchmarks', label: t('mijn_omgeving.toolruns.filter_benchmarks') },
    { value: 'simulators', label: t('mijn_omgeving.toolruns.filter_simulators') },
    { value: '__backtester__', label: t('mijn_omgeving.toolruns.filter_backtester') },
  ]);

  async function setCategory(value) {
    activeCategory.value = value;
    // The Backtester chip is a client-side filter on tool_key, not a category
    // the API understands. For others, we hit the backend filter.
    if (value === '__backtester__') {
      await loadToolruns({ category: null });
    } else {
      await loadToolruns({ category: value });
    }
  }

  onMounted(() => {
    loadToolruns();
  });

  // Pinned-first block (full view only).
  const pinnedRuns = computed(() => toolruns.value.filter((r) => r.pinned));
  // The main list excludes the pinned ones (they're already shown in the
  // "Vastgezet" block) and applies the client-side Backtester filter.
  const filteredMain = computed(() => {
    let arr = toolruns.value.filter((r) => !r.pinned);
    if (activeCategory.value === '__backtester__') {
      arr = arr.filter((r) => r.tool_key === 'bidding-strategy');
    }
    return arr;
  });
  const unpinnedDisplayed = computed(() => {
    if (props.limit) return filteredMain.value.slice(0, props.limit);
    return filteredMain.value;
  });

  // Snapshot rows: sort by latest status update (finished_at > started_at)
  // descending so the freshest activity bubbles up. Pinned/unpinned doesn't
  // matter on the Home Layer 3 strip — both kinds count as "recent toolruns".
  // Capped at `props.limit ?? 3`.
  const snapshotRuns = computed(() => {
    const max = props.limit ?? 3;
    const arr = [...(toolruns.value || [])];
    arr.sort((a, b) => _runUpdateMs(b) - _runUpdateMs(a));
    return arr.slice(0, max);
  });

  function _runUpdateMs(run) {
    const t = run?.finished_at || run?.started_at;
    if (!t) return 0;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  function snapshotUpdateTs(run) {
    return run?.finished_at || run?.started_at || null;
  }
  function snapshotRunTitle(run) {
    // Run-specific name takes precedence (e.g. "Battery 14d backtest"),
    // tool_label is the fall-through ("Onbalans-benchmark"), tool_key as
    // a last resort so we never render an empty row.
    const dn = (run?.display_name || '').trim();
    const tl = (run?.tool_label || '').trim();
    if (dn && dn !== tl) return dn;
    return tl || dn || run?.tool_key || '';
  }

  // Anything visible at all (pinned or unpinned) counts.
  const hasItems = computed(() => {
    if (props.snapshot) return snapshotRuns.value.length > 0;
    return pinnedRuns.value.length > 0 || unpinnedDisplayed.value.length > 0;
  });

  // Count of rendered runs, surfaced as `data-item-count` so QA can assert
  // list-vs-empty. Mirrors the two render modes: snapshot rows, or the
  // pinned + unpinned rows of the full view.
  const itemCount = computed(() =>
    props.snapshot
      ? snapshotRuns.value.length
      : pinnedRuns.value.length + unpinnedDisplayed.value.length
  );

  function mapStatus(status) {
    if (status === 'running' || status === 'pending') return 'running';
    if (status === 'failed') return 'failed';
    return 'ready';
  }

  // Snapshot helpers — kept inline rather than in ToolRunRow so that row stays
  // single-purpose (full /my-environment/toolruns page). The mapping mirrors
  // ToolRunRow's statusPillClass / statusLabel / startedLabel verbatim.
  function snapshotStatusLabel(run) {
    const s = mapStatus(run.status);
    if (s === 'running') return t('ui.tool_run_status_running');
    if (s === 'failed') return t('ui.tool_run_status_failed');
    return t('ui.tool_run_status_ready');
  }
  function snapshotStatusPillClass(run) {
    const s = mapStatus(run.status);
    if (s === 'running')
      return 'bg-theme-palette-sky/10 text-theme-palette-sky dark:bg-theme-palette-sky/30 dark:text-theme-palette-sky';
    if (s === 'failed')
      return 'bg-theme-palette-rose/10 text-theme-palette-rose dark:bg-theme-palette-rose/30 dark:text-theme-palette-rose';
    return 'bg-theme-palette-emerald/10 text-theme-palette-emerald dark:bg-theme-palette-emerald/30 dark:text-theme-palette-emerald';
  }
  function snapshotStartedLabel(startedAt) {
    const d = new Date(startedAt);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString();
  }

  function onOpen(run) {
    if (run.deep_link) router.push(run.deep_link);
  }

  function onRetry(run) {
    // Route to the tool with the run preselected + retry=1 query so the tool
    // page can pre-fill inputs and prompt re-run rather than auto-execute.
    if (!run.deep_link) return;
    const separator = run.deep_link.includes('?') ? '&' : '?';
    router.push(`${run.deep_link}${separator}retry=1`);
  }

  async function onTogglePin(run, next) {
    run.pinned = next;
    try {
      if (next) await pinTool(run.tool_key);
      else await unpinTool(run.tool_key);
    } catch (_) {
      run.pinned = !next;
    }
  }
</script>
